import { type AiTagId } from '@solinkcorp/api-types';
import { type Immutable } from 'immer';
import React, { useEffect, useMemo, useState } from 'react';
import { useControllerState } from 'solox';
import { IGNORE_THRESHOLD_WARNING } from '../../constants';
import { useHorizonStartMs } from '../../hooks/useHorizonStartMs';
import { useResolvedThreshold } from '../../hooks/useResolvedThreshold';
import { type SDMTimeWidget } from '../../layout/types';
import { averageZoneTimeSecondsSince } from '../../ServiceDisplayEventsController';
import { type CommonWidgetProps } from '../../types';
import BigValue from '../BigValue';
import WidgetCard, { type WidgetCardProps } from './WidgetCard';

interface TimeWidgetProps extends CommonWidgetProps {
  config: Immutable<SDMTimeWidget>;
  tagIds: AiTagId[];
}

const TimeWidget: React.FC<TimeWidgetProps> = (props) => {
  const {
    config,
    tagIds,
    eventsController,
    editing,
    hasConnectedTags,
    hasMultipleAssignments,
    hasLine,
    widget,
  } = props;

  const horizonStartMs = useHorizonStartMs(config.horizon, widget);

  // Subscribe to both maps so the widget re-renders for each state update.
  // `processEvent` splits ingestion into two separate `state.update()` calls — one for
  // `rawEvents` and one for `aiTagSummaryMap` — so subscribing to both ensures window-based
  // horizons (currentHour/currentDay/currentDayPart) pick up the change on the rawEvents-only
  // notification, not only after the follow-up summary update.
  const aiTagSummaryMap = useControllerState(
    eventsController.state,
    (state) => state.aiTagSummaryMap,
  );
  const rawEvents = useControllerState(eventsController.state, (state) => state.rawEvents);

  // Get the lowest entry timestamp (realtime) or the average duration in seconds (windowed).
  const lowestEntryTimestamp = useMemo<number | null>(() => {
    // Realtime widgets compute value from the live "in zone now" event list stored in the summary map.
    if (config.horizon === 'realtime') {
      // Collect all activeZoneEvents from all tagIds.
      const allActiveZoneEvents: string[] = [];
      tagIds?.forEach((tagId) => {
        const tagSummary = aiTagSummaryMap.get(tagId);
        if (tagSummary?.activeZoneEvents) {
          allActiveZoneEvents.push(...tagSummary.activeZoneEvents);
        }
      });

      // Extract entry timestamps and find the minimum (oldest entry).
      const entryTimestamps = allActiveZoneEvents.map((eventKey) => {
        const [entryTimestamp] = eventKey.split(':');
        return parseInt(entryTimestamp, 10);
      });
      return entryTimestamps.length > 0 ? Math.min(...entryTimestamps) : null;
    }
    // All window-based horizons (including '1h') compute the average over `rawEvents`
    if (horizonStartMs == null) return 0;
    return averageZoneTimeSecondsSince(rawEvents, tagIds, horizonStartMs);
  }, [config.horizon, tagIds, horizonStartMs, aiTagSummaryMap, rawEvents]);

  // Real-time counter state
  const [seconds, setSeconds] = useState<number>(0);

  // Update the counter every second
  useEffect(() => {
    if (lowestEntryTimestamp === null) {
      setSeconds(0);
      return;
    }

    if (config.horizon !== 'realtime') {
      // For all non-realtime horizons (1h, currentHour, currentDay, currentDayPart),
      // the selector already returns the average in seconds — display it directly.
      const elapsedSeconds = Math.floor(lowestEntryTimestamp);
      setSeconds(elapsedSeconds);
    } else {
      const currentTime = Date.now();
      const elapsedSeconds = Math.floor((currentTime - lowestEntryTimestamp) / 1000);
      setSeconds(elapsedSeconds);
      // Calculate initial seconds
      const updateCounter = () => {
        const currentTime = Date.now();
        const elapsedSeconds = Math.floor((currentTime - lowestEntryTimestamp) / 1000);
        setSeconds(elapsedSeconds);
      };

      // Update immediately
      updateCounter();

      // Set up interval to update every second
      const interval = setInterval(updateCounter, 1000);

      // Cleanup interval on unmount or when lowestEntryTimestamp changes
      return () => clearInterval(interval);
    }
  }, [lowestEntryTimestamp, config.horizon]);

  const { value: threshold, isFallback } = useResolvedThreshold(widget, config.threshold, 'TIME');
  const thresholdStr = `${Math.floor(threshold / 60)}m ${Math.floor(threshold % 60)
    .toString()
    .padStart(2, '0')}s`;
  const thresholdDebugLabel = isFallback
    ? `fallback: ${thresholdStr}`
    : `override: ${thresholdStr}`;

  let level: WidgetCardProps['level'] = 'normal';
  if (
    (!editing && seconds === undefined) ||
    hasMultipleAssignments ||
    hasLine ||
    threshold === IGNORE_THRESHOLD_WARNING
  ) {
    level = 'normal';
  } else if (threshold > 0 && seconds >= threshold) {
    level = 'critical';
  } else if (threshold > 0 && seconds >= threshold * 0.8) {
    level = 'warning';
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  const formatted: { value: number; unit: string }[] = [];
  formatted.push({ value: minutes, unit: 'm ' });
  formatted.push({ value: remainingSeconds, unit: 's' });

  return (
    <WidgetCard
      level={level}
      {...props}
      title={widget.title}
      thresholdDebugLabel={thresholdDebugLabel}
      horizonStartMs={horizonStartMs}
    >
      <BigValue scaleMultiplier={2}>
        {editing || !hasConnectedTags || hasMultipleAssignments || hasLine ? (
          <span className="p-0.5">---</span>
        ) : (
          <>
            <span className="p-0.5">{minutes}m</span>
            <span className="p-0.5">{remainingSeconds.toString().padStart(2, '0')}s</span>
          </>
        )}
      </BigValue>
    </WidgetCard>
  );
};

export default React.memo(TimeWidget);
